// Auth.tsx
import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast as message } from 'sonner';
import { supabaseClient } from '../utils/supabase.ts';
import { signOutLocally } from '../utils/impersonation';
import { GOOGLE_DRIVE_SCOPE } from '../features/docs/drive';
import loginPreview from '../assets/login-preview.png';
import './Auth.css';

const PENDING_OAUTH_REDIRECT_KEY = 'sky_crm_pending_oauth_redirect';
// Keep the password flow implementation ready for a future rollout while
// presenting Google as the only login option today.
const SHOW_PASSWORD_LOGIN = false;

function getSafeRedirectPath(path: string | null): string {
    if (!path || !path.startsWith('/') || path.startsWith('//')) {
        return '/';
    }
    if (path.startsWith('/auth/')) {
        return '/';
    }
    return path;
}

export const ResetPassword: React.FC = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const [password, setPassword] = useState<string>('');
    const [loading, setLoading] = useState<boolean>(false);
    const [statefulError, setError] = useState<string>('');
    const [successMessage, setSuccessMessage] = useState<string>('');
    // Get token and type from URL params
    const queryParams = new URLSearchParams(location.hash.slice(1));
    let errorType: string | null = queryParams.get('error');
    // transform access_denied to Access Denied (title case)
    errorType = errorType ? errorType.replace(/_/g, ' ').replace(/\w\S*/g, (w) => (w.replace(/^\w/, (c) => c.toUpperCase()))) : null;
    const errorCode: string | null = queryParams.get('error_code');
    const errorDescription: string | null = queryParams.get('error_description');
    const accessToken: string | null = queryParams.get('access_token');
    const refreshToken: string | null = queryParams.get('refresh_token');

    const errorMsg = `Error: ${errorType ? errorType : 'None'} - ${errorDescription? errorDescription : 'No Error Message'}`;
    if (errorType || errorDescription) {
        console.error("Server Error:", errorMsg);
        message.error(errorMsg);
    }
    useEffect(() => {
        async function setSession(): Promise<void> {
            if (accessToken && refreshToken) {
                const { error: setSessionError } = await supabaseClient.auth.setSession({
                    access_token: accessToken,
                    refresh_token: refreshToken,
                });
                if (setSessionError) {
                    console.error('Error setting session:', setSessionError.message);
                    setError(setSessionError.message);
                }
            }
        }
        setSession();
    }, [accessToken, refreshToken]);

    // Handle password update
    const handlePasswordReset = async (): Promise<void> => {
        setLoading(true);
        const { error } = await supabaseClient.auth.updateUser({ password });
        if (error) {
            console.error('Password update error:', error.message);
            setError(error.message);
        } else {
            setSuccessMessage('Password updated successfully!');
            navigate('/'); // Redirect to the home
        }
        setLoading(false);
    };


    useEffect(() => {
        if (statefulError) {
            message.error(statefulError);
        }
    }, [statefulError]);

    useEffect(() => {
        if (successMessage) {
            message.success(successMessage);
        }
    }, [successMessage]);

    const isError: boolean = !!(errorType || errorDescription || errorCode || statefulError);

    return (
      <div className="sky-auth-container">
        <div className="sky-auth-card">
          <div className="sky-auth-logo">
            <svg width="40" height="40" viewBox="0 0 34 34" fill="none">
              <rect width="34" height="34" rx="8" fill="var(--accent)" />
              <g fill="white">
                <rect x="4" y="22" width="4" height="6" rx="0.5" />
                <rect x="9" y="18" width="3" height="10" rx="0.5" />
                <rect x="13" y="10" width="4" height="18" rx="0.5" />
                <rect x="14.5" y="6" width="1" height="5" rx="0.5" />
                <rect x="17" y="15" width="3" height="13" rx="0.5" />
                <rect x="21" y="19" width="4" height="9" rx="0.5" />
                <rect x="26" y="16" width="3" height="12" rx="0.5" />
                <rect x="4" y="28" width="26" height="1.5" rx="0.5" />
              </g>
            </svg>
          </div>
          <h3 className="sky-auth-title">Reset Password</h3>

          <div className="sky-auth-field">
            <label htmlFor="password-input">New Password</label>
            <input
              id="password-input"
              placeholder="Enter your new password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={isError}
              className="sky-auth-input"
            />
          </div>
          {statefulError && <p className="sky-auth-error">{statefulError}</p>}
          <button
            className="sky-btn sky-btn-primary sky-auth-submit"
            onClick={handlePasswordReset}
            disabled={loading || isError}
          >
            {loading ? <span className="sky-spinner" style={{ width: 16, height: 16, borderWidth: 2 }} /> : 'Reset Password'}
          </button>
        </div>
      </div>
    );
};

export const Login: React.FC = () => {
    const [email, setEmail] = useState<string>('');
    const [password, setPassword] = useState<string>('');
    const [loading, setLoading] = useState<boolean>(false);
    const [resetLoading, setResetLoading] = useState<boolean>(false);
    const [errorMessage, setErrorMessage] = useState<string>('');
    const [successMessage, setSuccessMessage] = useState<string>('');
    const location = useLocation();
    const navigate = useNavigate();
    const fromLocation: string = location.state?.from?.pathname || '/';
    const fullFromURL: string = `${fromLocation}${location.state?.from?.search || ''}${location.state?.from?.hash || ''}`;

    useEffect(() => {
        let cancelled = false;

        async function finishOAuthRedirect(): Promise<void> {
            const { data: { session } } = await supabaseClient.auth.getSession();
            if (!session || cancelled) return;

            const pendingRedirect = getSafeRedirectPath(sessionStorage.getItem(PENDING_OAUTH_REDIRECT_KEY) ?? fullFromURL);
            sessionStorage.removeItem(PENDING_OAUTH_REDIRECT_KEY);
            navigate(pendingRedirect, { replace: true });
        }

        finishOAuthRedirect();

        const { data: authListener } = supabaseClient.auth.onAuthStateChange((_event, session) => {
            if (!session) return;
            const pendingRedirect = getSafeRedirectPath(sessionStorage.getItem(PENDING_OAUTH_REDIRECT_KEY) ?? fullFromURL);
            sessionStorage.removeItem(PENDING_OAUTH_REDIRECT_KEY);
            navigate(pendingRedirect, { replace: true });
        });

        return () => {
            cancelled = true;
            authListener.subscription.unsubscribe();
        };
    }, [fullFromURL, navigate]);

    const handleLogin = async (): Promise<void> => {
        setLoading(true);
        const { error } = await supabaseClient.auth.signInWithPassword({
            email,
            password,
        });
        if (error) {
            setErrorMessage(error.message);
        } else {
            setSuccessMessage('Logged in successfully!');
            message.success('Logged in successfully!');
            navigate(fullFromURL, { replace: true });
        }
        setLoading(false);
    };

    const handleGoogleLogin = async (): Promise<void> => {
        setLoading(true);
        setErrorMessage('');
        sessionStorage.setItem(PENDING_OAUTH_REDIRECT_KEY, getSafeRedirectPath(fullFromURL));

        // Request Drive scope + offline access at login so "Sign in with
        // Google" also powers Drive features (docs tabs, submission composer)
        // without a separate "connect Google" step. The refresh token lets us
        // mint new access tokens after the 1-hour Google token expires.
        const { error } = await supabaseClient.auth.signInWithOAuth({
            provider: 'google',
            options: {
                redirectTo: window.location.origin,
                scopes: GOOGLE_DRIVE_SCOPE,
                queryParams: {
                    access_type: 'offline',
                    // Always show the account chooser. Drive access follows the
                    // selected Google account, which may differ between users.
                    prompt: 'consent select_account',
                },
            },
        });

        if (error) {
            sessionStorage.removeItem(PENDING_OAUTH_REDIRECT_KEY);
            setErrorMessage(error.message);
            setLoading(false);
        }
    };

    const handleForgotPassword = async (): Promise<void> => {
        setResetLoading(true);
        const { error } = await supabaseClient.functions.invoke('gmail-xt-password-bootstrap', {
            body: { email },
        });
        if (error) {
            console.error('Password setup error:', error.message);
            setErrorMessage(error.message);
        } else {
            setSuccessMessage('If this email is allowed, password setup instructions were sent.');
        }
        setResetLoading(false);
    };

    useEffect(() => {
        if (errorMessage) {
            message.error(errorMessage);
        }
    }, [errorMessage]);

    useEffect(() => {
        if (successMessage) {
            message.success(successMessage);
        }
    }, [successMessage]);

    return (
      <div className="sky-auth-container sky-auth-container--split">
        <aside className="sky-auth-showcase" aria-hidden="true">
          <div className="sky-auth-showcase-art">
            <img src={loginPreview} alt="" className="sky-auth-showcase-shot" />
          </div>
          <div className="sky-auth-showcase-copy">
            <h2>Every deal, one portal</h2>
            <p className="sky-auth-showcase-tags">Submissions · Approvals · Funded</p>
            <p>Track merchants from lead to funded, send offers in two clicks, and keep every funder reply in one place.</p>
          </div>
        </aside>
        <main className="sky-auth-card sky-auth-card--split">
          <div className="sky-auth-logo">
            <svg width="40" height="40" viewBox="0 0 34 34" fill="none">
              <rect width="34" height="34" rx="8" fill="var(--accent)" />
              <g fill="white">
                <rect x="4" y="22" width="4" height="6" rx="0.5" />
                <rect x="9" y="18" width="3" height="10" rx="0.5" />
                <rect x="13" y="10" width="4" height="18" rx="0.5" />
                <rect x="14.5" y="6" width="1" height="5" rx="0.5" />
                <rect x="17" y="15" width="3" height="13" rx="0.5" />
                <rect x="21" y="19" width="4" height="9" rx="0.5" />
                <rect x="26" y="16" width="3" height="12" rx="0.5" />
                <rect x="4" y="28" width="26" height="1.5" rx="0.5" />
              </g>
            </svg>
          </div>
          <h1 className="sky-auth-title">Log in to Skyline</h1>
          <p className="sky-auth-subtitle">Use your @skylinecaps.com Google account.</p>

          <button
            className="sky-btn sky-btn-google sky-auth-submit"
            onClick={handleGoogleLogin}
            disabled={loading}
          >
            {loading ? (
              <span className="sky-spinner" style={{ width: 16, height: 16, borderWidth: 2 }} />
            ) : (
              <>
                <svg width="16" height="16" viewBox="0 0 18 18" aria-hidden="true">
                  <path fill="#4285F4" d="M17.6 9.2c0-.6-.1-1.1-.2-1.6H9v3.1h4.8c-.2 1-.8 1.9-1.7 2.5v2h2.7c1.6-1.5 2.8-3.7 2.8-6z" />
                  <path fill="#34A853" d="M9 18c2.4 0 4.4-.8 5.8-2.1l-2.7-2c-.8.5-1.8.8-3.1.8-2.3 0-4.3-1.6-5-3.7H1.2v2.1C2.6 16 5.6 18 9 18z" />
                  <path fill="#FBBC05" d="M4 11c-.2-.6-.3-1.3-.3-2s.1-1.4.3-2V4.9H1.2C.4 6.1 0 7.5 0 9s.4 2.9 1.2 4.1L4 11z" />
                  <path fill="#EA4335" d="M9 3.6c1.3 0 2.5.5 3.4 1.3l2.5-2.5C13.4.9 11.4 0 9 0 5.6 0 2.6 2 1.2 4.9L4 7c.7-2.1 2.7-3.4 5-3.4z" />
                </svg>
                Continue with Google
              </>
            )}
          </button>

          {SHOW_PASSWORD_LOGIN && (
            <>
              <div className="sky-auth-divider">
                <span>or use password</span>
              </div>

              <div className="sky-auth-field">
                <label htmlFor="email-input">Email</label>
                <input
                  id="email-input"
                  placeholder="Enter your email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="sky-auth-input"
                  onKeyDown={(e) => e.key === 'Enter' && handleLogin()}
                />
              </div>
              <div className="sky-auth-field">
                <label htmlFor="password-input">Password</label>
                <input
                  id="password-input"
                  placeholder="Enter your password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="sky-auth-input"
                  onKeyDown={(e) => e.key === 'Enter' && handleLogin()}
                />
              </div>

              <button
                className="sky-btn sky-btn-primary sky-auth-submit"
                onClick={handleLogin}
                disabled={loading}
              >
                {loading ? <span className="sky-spinner" style={{ width: 16, height: 16, borderWidth: 2 }} /> : 'Log in'}
              </button>
              <button
                className="sky-auth-forgot"
                onClick={handleForgotPassword}
                disabled={resetLoading}
              >
                {resetLoading ? 'Sending...' : 'Create or reset password'}
              </button>
            </>
          )}
          <p className="sky-auth-help">
            Trouble logging in? Reach out to <a href="mailto:admin@skylinecaps.com">admin@skylinecaps.com</a> for assistance.
          </p>
        </main>
      </div>
    );
};

export const Logout: React.FC = () => {
    const navigate = useNavigate();

    useEffect(() => {
        const signOut = async (): Promise<void> => {
            try {
                await signOutLocally();
            } finally {
                navigate('/auth/login', { replace: true });
            }
        };
        void signOut();
    }, [navigate]);

    return (
        <div className="sky-auth-container">
            <div className="sky-auth-card" style={{ textAlign: 'center' }}>
                <div className="sky-spinner" style={{ margin: '0 auto 16px' }} />
                <p style={{ color: 'var(--text-2)' }}>Logging out...</p>
            </div>
        </div>
    );
};
